import { useState, useEffect } from "react";
import { WifiOff, RefreshCw, Smartphone, Apple } from "lucide-react";
import UpdateBanner from "./UpdateBanner";
import { ClubSplash } from "./ClubSplash";
import { CLUB_LOGO } from "../../club-site/siteData";

const API_URL = process.env.REACT_APP_BACKEND_URL;
const SPLASH_MIN_MS = 3500;
const SPLASH_MAX_MS = 4000;
const HEALTH_TIMEOUT_MS = 15000;      // 15s — slow mobile networks.
const MAX_PROBE_ATTEMPTS = 2;         // Retry transient failures before declaring offline.

/** Detect that we're running inside the installed PWA or the Android APK
 *  wrapper. Anything else (regular Chrome/Safari tab) is "web" and gets the
 *  download gate. The APK's WebView injects a custom UA token so we can
 *  detect it deterministically. */
const isInstalledApp = () => {
  if (typeof window === "undefined") return true; // SSR — assume installed.
  try {
    if (window.matchMedia?.("(display-mode: standalone)").matches) return true;
    if (window.navigator?.standalone === true) return true; // iOS legacy PWA
    if (/LefteriaFC-App/i.test(navigator.userAgent || "")) return true;
    // Allow the user to bypass the gate during local dev / debugging.
    if (new URLSearchParams(window.location.search).get("allow_web") === "1") return true;
    if (localStorage.getItem("app_allow_web") === "1") return true;
  } catch (_e) { /* defensive — never crash the shell */ }
  return false;
};

/**
 * Persists the "returning user" flag AFTER first paint so the splash heading
 * doesn't flip from "ΛΕΥΤΕΡΙΑ FC" to "Καλωσορισες ξανα" mid-render under
 * React StrictMode's double-invocation.
 */
const SplashReturningMarker = () => {
  useEffect(() => {
    try { localStorage.setItem("lefteria_returning", "1"); } catch {}
  }, []);
  return null;
};

/** Full-screen "Download the App" gate. Shown when a regular web browser hits
 *  `/app` (or any sub-route) — the user must install the PWA / APK to view
 *  content. Mirrors the styling of the server-rendered share gate. */
const WebAccessGate = () => {
  const [info, setInfo] = useState(null);
  useEffect(() => {
    fetch(`${API_URL}/api/app/info`).then(r => r.ok ? r.json() : null).then(setInfo).catch(() => {});
  }, []);
  const androidHref = `${API_URL}/api/app/android`;
  const iosHref = info?.ios_app_store_url || null;
  return (
    <div className="fixed inset-0 bg-[#050505] z-[9999] overflow-hidden" data-testid="mobile-web-gate">
      <div className="absolute inset-0" style={{ background: "radial-gradient(80% 70% at 50% 22%, rgba(245,166,35,0.18), rgba(0,0,0,0) 60%), linear-gradient(180deg, rgba(0,0,0,0.0), rgba(0,0,0,0.85))" }} />
      <div className="relative h-full w-full flex flex-col items-center justify-center px-6 text-center">
        <div className="relative">
          <div className="absolute inset-0 bg-[#F5A623] blur-3xl opacity-25 animate-pulse" />
          <img src={CLUB_LOGO} alt="ΛΕΥΤΕΡΙΑ FC" className="relative w-28 h-28 mx-auto drop-shadow-[0_8px_28px_rgba(245,166,35,0.35)]" />
        </div>
        <span className="mt-6 text-[10px] tracking-[0.32em] uppercase text-[#F5A623] border border-[#F5A623]/40 rounded-full px-3 py-1.5">
          Μονο στην Εφαρμογη
        </span>
        <h1 className="font-['Bebas_Neue'] text-3xl text-white tracking-[0.05em] mt-5 leading-tight">
          Η Εφαρμογη της Λευτεριας FC
        </h1>
        <p className="text-zinc-400 text-sm mt-3 max-w-xs leading-relaxed">
          Το περιεχομενο της εφαρμογης δεν ειναι διαθεσιμο απο τον browser. Κατεβασε την εφαρμογη για να συνεχισεις.
        </p>
        <div className="mt-7 flex flex-col gap-2.5 w-full max-w-xs">
          <a
            href={androidHref}
            className="bg-[#F5A623] text-black font-semibold py-3 rounded-xl flex items-center justify-center gap-2 active:scale-[0.98] transition-transform"
            data-testid="web-gate-cta-android"
          >
            <Smartphone size={18} /> Android (APK)
          </a>
          {iosHref ? (
            <a
              href={iosHref}
              className="bg-white/[0.06] border border-white/15 text-white font-semibold py-3 rounded-xl flex items-center justify-center gap-2 backdrop-blur active:scale-[0.98] transition-transform"
              data-testid="web-gate-cta-ios"
            >
              <Apple size={18} /> iPhone (App Store)
            </a>
          ) : (
            <button
              type="button"
              onClick={() => alert("Η εκδοση για iPhone ερχεται συντομα.")}
              className="bg-white/[0.06] border border-white/15 text-white font-semibold py-3 rounded-xl flex items-center justify-center gap-2 backdrop-blur active:scale-[0.98] transition-transform"
              data-testid="web-gate-cta-ios"
            >
              <Apple size={18} /> iPhone (Συντομα)
            </button>
          )}
        </div>
        <a
          href="/"
          className="mt-8 text-[11px] text-zinc-500 hover:text-zinc-300 underline underline-offset-2"
          data-testid="web-gate-back-link"
        >
          Επιστροφη στο επισημο site της Λευτεριας
        </a>
      </div>
    </div>
  );
};

/**
 * Wraps the mobile app with a splash + offline guard.
 *
 * Speed pass (iter 61): the health probe now runs IN PARALLEL with the splash
 * animation instead of starting after a 1200ms wait. We hold the splash for a
 * 500ms minimum (so the animation finishes), then dismiss as soon as the probe
 * succeeds. Net wait drops from ~1.7s → ~500ms when the network is fast.
 *
 * Iter 65: when accessed from a regular web browser (NOT the installed PWA or
 * the Android APK webview), render the "Download the App" gate instead of the
 * app itself. Shared content URLs (`/api/share/...`) are gated server-side.
 */
const MobileAppShell = ({ children }) => {
  const [installed] = useState(() => isInstalledApp());
  const [phase, setPhase] = useState("splash"); // splash | ready | offline
  const [tries, setTries] = useState(0);

  useEffect(() => {
    if (!installed) return undefined;
    let cancelled = false;
    const startedAt = Date.now();

    const probe = async () => {
      let lastErr = null;
      for (let attempt = 1; attempt <= MAX_PROBE_ATTEMPTS; attempt++) {
        try {
          const controller = new AbortController();
          const to = setTimeout(() => controller.abort(), HEALTH_TIMEOUT_MS);
          const r = await fetch(`${API_URL}/api/health?t=${Date.now()}`, { signal: controller.signal, cache: "no-store" });
          clearTimeout(to);
          if (!r.ok) throw new Error(`bad status ${r.status}`);
          return { ok: true };
        } catch (e) {
          lastErr = e;
          if (attempt < MAX_PROBE_ATTEMPTS) {
            await new Promise(res => setTimeout(res, 800));
          }
        }
      }
      console.warn("[MobileAppShell] offline guard tripped:", lastErr);
      return { ok: false };
    };

    (async () => {
      const result = await probe();
      if (cancelled) return;
      // Honour the minimum splash duration so the animation finishes cleanly.
      const elapsed = Date.now() - startedAt;
      const remaining = Math.max(0, SPLASH_MIN_MS - elapsed);
      if (remaining > 0) await new Promise(res => setTimeout(res, remaining));
      if (cancelled) return;
      setPhase(result.ok ? "ready" : "offline");
    })();

    // Hard ceiling — never block UI longer than SPLASH_MAX_MS even if probe stalls.
    const ceiling = setTimeout(() => {
      if (!cancelled) setPhase((p) => (p === "splash" ? "ready" : p));
    }, SPLASH_MAX_MS);

    return () => { cancelled = true; clearTimeout(ceiling); };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [tries, installed]);

  // React to runtime online/offline changes (background → foreground, wifi drop, etc.)
  useEffect(() => {
    if (!installed) return undefined;
    const onOnline = () => { if (phase === "offline") setTries(t => t + 1); };
    const onOffline = () => setPhase("offline");
    window.addEventListener("online", onOnline);
    window.addEventListener("offline", onOffline);
    return () => {
      window.removeEventListener("online", onOnline);
      window.removeEventListener("offline", onOffline);
    };
  }, [phase, installed]);

  // Iter 65: regular browsers see the download gate AFTER hooks have run so
  // React's rules-of-hooks invariant is preserved across renders.
  if (!installed) {
    return <WebAccessGate />;
  }

  if (phase === "splash") {
    return <ClubSplash />;
  }

  if (phase === "offline") {
    return (
      <div className="fixed inset-0 bg-[#0a0a0a] flex flex-col items-center justify-center z-[9999] px-6 text-center" data-testid="mobile-offline">
        <img src={CLUB_LOGO} alt="ΛΕΥΤΕΡΙΑ FC" className="w-24 h-24 opacity-70" />
        <div className="mt-8 w-16 h-16 rounded-full bg-red-500/15 border border-red-500/30 flex items-center justify-center">
          <WifiOff size={28} className="text-red-400" />
        </div>
        <h2 className="font-['Bebas_Neue'] text-2xl text-white tracking-[0.05em] mt-5">Δεν υπάρχει σύνδεση</h2>
        <p className="text-zinc-400 text-sm mt-2 max-w-xs leading-relaxed">
          Η εφαρμογή χρειάζεται ενεργή σύνδεση internet για να φορτώσει το περιεχόμενο της Λευτεριά FC.
        </p>
        <button
          onClick={() => { setPhase("splash"); setTries(t => t + 1); }}
          className="mt-6 bg-[#F5A623] text-black font-semibold px-6 py-3 rounded-xl flex items-center gap-2 hover:bg-[#e09620] transition-colors"
          data-testid="offline-retry-btn"
        >
          <RefreshCw size={16} /> Δοκιμή ξανά
        </button>
        <p className="text-[10px] text-zinc-600 mt-8">Ελέγξτε Wi-Fi ή δεδομένα κινητής τηλεφωνίας</p>
      </div>
    );
  }

  return (
    <>
      <UpdateBanner />
      {children}
    </>
  );
};

export default MobileAppShell;
